// Third-party
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import get from 'lodash/get';
import { useSelector } from 'react-redux';

// Components
import { CloseButton, SecondaryButton, TertiaryButton } from '../../../components/buttons';
import { MenuToggle } from '../../../components/buttons';
import { Typography } from '@capintel/component-lib/typography';

// Types
import ComparisonItem from '@/types/comparison-item';
import RiskProfileItem from '@/types/risk-profile-item';
import ThemeMode from '@/types/theme-mode';
import User from '@/types/user';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { StateOmniRiskProfileConfig } from '@/types/risk-profiles/omni/omni-risk-profile-context-types';

// Utils
import RiskProfileVisualizer from './RiskProfileVisualizer';
import RiskProfileRuleVisualizer from '@/views/components/charts/risk-profile-rule-visualizer/RiskProfileRuleVisualizer';
import { formatLocalizedString, FormattedMessage, formatMessage } from '@/utils/i18n';
import { getOriginalRiskProfile, getRiskProfileRuleData, isSummaryView } from '@/utils/risk-profiles';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { ReduxState } from '@/redux/modules/types';

interface Props {
  profile: RiskProfileItem | StateOmniRiskProfileConfig;
  readOnly?: boolean;
  onPortfolio?: boolean;
  comparedItem?: ComparisonItem;
  onRemoveProfile?: () => void;
  onChangeProfile?: () => void;
  onAddToComparison?: () => void;
  onSelectComparisonRiskProfile?: () => void;
  onClose?: () => void;
  theme?: ThemeMode;
  user: User;
}

const RiskProfileCompare = (props: Props) => {
  const {
    readOnly,
    profile,
    comparedItem,
    onPortfolio,
    theme,
    user,
    onChangeProfile,
    onRemoveProfile,
    onAddToComparison,
    onSelectComparisonRiskProfile,
    onClose,
  } = props;

  // to be able to add a portfolio risk profile to a comparison,
  // it must be still available in the questionnaires in order for the alternativeModels feature to work properly
  const questionnaires = useSelector((state: ReduxState) => state.kycQuestionnaire.questionnaires);
  const portfolioRiskProfileStillAvailable = !!getOriginalRiskProfile(profile, questionnaires);

  const canAddToComparison = onAddToComparison && !readOnly && !!onPortfolio && portfolioRiskProfileStillAvailable;
  const canSelectComparisonRiskProfile = onSelectComparisonRiskProfile && !readOnly && !!onPortfolio;
  const canChange = onChangeProfile && !readOnly && !onPortfolio;
  const canRemove = onRemoveProfile && !readOnly && !onPortfolio;
  const isOmniRiskProfile = userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE) && 'isOmniRiskProfile' in profile && profile.isOmniRiskProfile;

  const getMenuItems = () =>
    [
      {
        value: formatMessage('Common.Edit'),
        icon: 'icon-pencil',
        onClick: onChangeProfile,
        hidden: !canChange || isOmniRiskProfile,
      },
      {
        value: formatMessage('RiskProfile.AddToComparison'),
        icon: 'icon-plus-med',
        onClick: onAddToComparison,
        hidden: !canAddToComparison,
      },
      {
        value: formatMessage('Common.Remove'),
        icon: 'icon-remove-new',
        className: 'destructive',
        onClick: onRemoveProfile,
        hidden: !canRemove,
      },
      {
        value: formatMessage('Common.Deselect'),
        icon: 'icon-minus-med',
        onClick: onSelectComparisonRiskProfile,
        hidden: !canSelectComparisonRiskProfile,
      },
    ].filter(i => !i.hidden);

  const getMenuOptions = () => {
    const options = getMenuItems().map(item => (
      <Dropdown.Item key={item.value} role="menuitem" as="button" className={classNames(item.className)} onClick={() => item.onClick && item.onClick()}>
        <i className={item.icon} />
        <span>{item.value}</span>
      </Dropdown.Item>
    ));

    if (!options.length) {
      return null;
    }

    return <MenuToggle options={options} alignRight ariaLabel={formatMessage('RiskProfile.MenuToggle')} />;
  };

  const rulesData = profile.rules.map(rule => ({
    rule,
    data: getRiskProfileRuleData(rule, comparedItem, undefined, user),
  }));

  const showSummaryView = isSummaryView(profile);

  return (
    <div className="risk-profile-compare">
      <div className="risk-profile-data">
        {onClose && <CloseButton onClick={onClose} aria-label={formatMessage('Common.Close')} />}

        {theme === ThemeMode.LIGHT && getMenuOptions()}

        <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
          {formatLocalizedString(profile.title)}
        </Typography>

        <span className="profile-subtext">
          {formatMessage('RiskProfile.ComparedTo')}
          {comparedItem ? formatLocalizedString(comparedItem.name) : formatMessage('RiskProfile.AllItems')}
        </span>

        {showSummaryView && (
          <div className="visualizers">
            <RiskProfileVisualizer rulesData={rulesData} item={comparedItem} />
          </div>
        )}

        {!showSummaryView && (
          <div className="visualizers">
            {rulesData.map((ruleData, index) => (
              <RiskProfileRuleVisualizer
                key={`${ruleData.rule.toString()}-${index}`}
                ruleData={ruleData.data}
                rule={ruleData.rule}
                singleRuleLayout={profile.rules.length === 1}
              />
            ))}
          </div>
        )}
      </div>

      {theme !== ThemeMode.LIGHT && (
        <div className="profile-actions">
          {canChange && (
            <SecondaryButton compact dark onClick={onChangeProfile}>
              <i className="icon-arrow-left" />
              <FormattedMessage id="RiskProfile.ChangeRiskProfile" />
            </SecondaryButton>
          )}

          {canAddToComparison && (
            <SecondaryButton compact dark onClick={onAddToComparison}>
              <FormattedMessage id="RiskProfile.AddToComparison" />
            </SecondaryButton>
          )}

          {canRemove && (
            <TertiaryButton compact dark onClick={onRemoveProfile}>
              <i className="icon-cross-thin" />
              <FormattedMessage id="Common.Remove" />
            </TertiaryButton>
          )}

          {canSelectComparisonRiskProfile && (
            <TertiaryButton compact dark onClick={onSelectComparisonRiskProfile}>
              <FormattedMessage id="RiskProfile.SelectComparisonRiskProfile" />
            </TertiaryButton>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskProfileCompare;
